import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Plus, Trash2, Video, Loader2, Link as LinkIcon } from 'lucide-react';
import reviewConstants from '../../../constants/review.constants';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';

function ReviewRecordLinksModal({ links, isOpen, onSave, onClose, isSaveLoading }) {
  const [linkList, setLinkList] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const initialLinks = links && links.length > 0 ? [...links] : [''];
      setLinkList(
        initialLinks.map((link, index) => ({
          id: `link-${Date.now()}-${index}`,
          value: link,
        })),
      );
    }
  }, [isOpen, links]);

  const handleLinkInput = (index, value) => {
    const newLinks = [...linkList];
    newLinks[index] = { ...newLinks[index], value };
    setLinkList(newLinks);
  };

  const addNewLink = () => {
    const newLink = {
      id: `link-${Date.now()}-${linkList.length}`,
      value: '',
    };
    setLinkList([...linkList, newLink]);
  };

  const removeLink = (index) => {
    if (linkList.length > 1) {
      const newLinks = linkList.filter((_, i) => i !== index);
      setLinkList(newLinks);
    }
  };

  const clearLink = (index) => {
    const newLinks = [...linkList];
    newLinks[index] = { ...newLinks[index], value: '' };
    setLinkList(newLinks);
  };

  const validateUrl = (url) => {
    if (!url.trim()) return true;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    const validLinks = linkList
      .map((linkObj) => linkObj.value.trim())
      .filter((link) => link.length > 0)
      .filter((link) => validateUrl(link));

    onSave(validLinks);
  };

  const handleClose = () => {
    setLinkList([]);
    onClose();
  };

  const isSaveDisabled =
    linkList.every((linkObj) => !linkObj.value.trim()) ||
    linkList.some((linkObj) => linkObj.value.trim() && !validateUrl(linkObj.value));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative bg-background rounded-lg shadow-lg w-full max-w-md mx-4"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Video className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Review Records</h2>
              <p className="text-sm text-muted-foreground">Add or manage recording links</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-3 max-h-[400px] overflow-y-auto">
          {linkList.map((linkObj, index) => {
            const hasError = linkObj.value.trim() && !validateUrl(linkObj.value);
            return (
              <div key={linkObj.id} className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={linkObj.value}
                      onChange={(e) => handleLinkInput(index, e.target.value)}
                      placeholder={`Recording link ${index + 1}`}
                      className={cn(
                        'w-full pl-9 pr-10 py-2 rounded-md text-sm',
                        'bg-background border',
                        'focus:outline-none focus:ring-2 focus:border-transparent',
                        hasError
                          ? 'border-destructive focus:ring-destructive'
                          : 'border-input focus:ring-ring',
                      )}
                    />
                    {linkObj.value.trim() && (
                      <button
                        type="button"
                        onClick={() => clearLink(index)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {linkList.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLink(index)}
                      className="h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {hasError && (
                  <p className="text-xs text-destructive ml-1">
                    {reviewConstants.ERROR_MESSAGES.URL_IS_INVALID}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Link Button */}
        <div className="px-6 pb-4">
          <Button variant="outline" onClick={addNewLink} className="w-full border-dashed">
            <Plus className="h-4 w-4 mr-2" />
            Add Another Link
          </Button>
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-3 px-6 py-4 border-t"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaveDisabled || isSaveLoading}>
            {isSaveLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

ReviewRecordLinksModal.propTypes = {
  links: PropTypes.arrayOf(PropTypes.string),
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSaveLoading: PropTypes.bool,
};

ReviewRecordLinksModal.defaultProps = {
  links: [],
  isSaveLoading: false,
};

export default ReviewRecordLinksModal;
